import { createContext, useContext, useEffect, useState, ReactNode } from 'react'

import { me } from '@/api/client'
import { clearToken, getStoredUser, setStoredUser, StoredUser } from '@/lib/token'

interface AuthContextValue {
  user: StoredUser | null
  loading: boolean
  logout: () => void
  refresh: () => Promise<void>
  setAuthenticatedUser: (user: StoredUser) => void
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  logout: () => {},
  refresh: async () => {},
  setAuthenticatedUser: () => {},
})

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<StoredUser | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = async () => {
    try {
      const data = await me()
      setUser(data.user)
      setStoredUser(data.user)
    } catch {
      setUser(null)
    }
  }

  const logout = () => {
    clearToken()
    setUser(null)
    window.location.href = '/login'
  }

  const setAuthenticatedUser = (nextUser: StoredUser) => {
    setUser(nextUser)
    setStoredUser(nextUser)
  }

  useEffect(() => {
    const stored = getStoredUser()
    if (stored) {
      setUser(stored)
      refresh().finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, logout, refresh, setAuthenticatedUser }}>
      {children}
    </AuthContext.Provider>
  )
}
